import { useState, useCallback } from "react";
import axios from "axios";
import { getAuthHeader } from "../utils/authHeader";

export interface QuotaInfo {
    used: number;
    limit: number;
    available: number;
}

export interface QuotaStatus {
    storage: QuotaInfo;
    transfer: QuotaInfo;
}

export const useQuotaCheck = () => {
    const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchQuotaStatus = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const authHeader = await getAuthHeader();
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/usage/quota-status`,
                { headers: authHeader }
            );
            setQuotaStatus(response.data);
        } catch (err: any) {
            setError(
                err.response?.data?.message || "Failed to fetch quota status"
            );
            console.error("Error fetching quota status:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const checkStorageQuota = useCallback(
        async (fileSize: number, noOfVideos?: number, noOfImages?: number, uploadType?: string, postType?: string, originalVideoDuration?: number): Promise<{ storageOk: boolean; videoLimitOk: boolean }> => {
            try {
                const authHeader = await getAuthHeader();
                // Backend expects size in MB; convert from bytes to MB
                const fileSizeMB = fileSize / (1024 * 1024);

                // Only include noOfImages for listing uploads (not for assets)
                const isListingUpload = uploadType !== "asset" && uploadType !== "assetImage" && uploadType !== "logo";

                const payload: { fileSize: number; noOfImages?: number } = { fileSize: fileSizeMB };
                if (isListingUpload && noOfImages !== undefined) {
                    payload.noOfImages = noOfImages;
                }

                const response = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/usage/check-storage`,
                    payload,
                    { headers: authHeader }
                );

                let storageOk = response.data === true;
                // videoLimitOk only carries the ZapCap-seconds verdict and is
                // surfaced for the captioned-video flow. For every other video
                // upload, a /check-video-limit failure is folded into storageOk
                // (existing behavior) so callers keep showing the generic
                // "storage" PlanLimitReached.
                let videoLimitOk = true;
                if(noOfVideos !== undefined && noOfVideos > 0) {
                    const videoLimitPayload: { noOfVideos: number; postType?: string; originalVideoDuration?: number } = { noOfVideos };
                    if (postType) {
                        videoLimitPayload.postType = postType;
                    }
                    if (originalVideoDuration !== undefined) {
                        videoLimitPayload.originalVideoDuration = Math.ceil(originalVideoDuration);
                    }
                    const videoLimit = await axios.post(
                        `${process.env.REACT_APP_BACKEND_URL}/usage/check-video-limit`,
                        videoLimitPayload,
                        { headers: authHeader }
                    );
                    const videoLimitResp = videoLimit.data === true;
                    if (postType === "Caption Video") {
                        videoLimitOk = videoLimitResp;
                    } else if (!videoLimitResp) {
                        storageOk = false;
                    }
                }
                return { storageOk, videoLimitOk };
            } catch (err: any) {
                console.error("Error checking storage quota:", err);
                return { storageOk: false, videoLimitOk: true };
            }
        },
        []
    );

    const checkTransferQuota = useCallback(
        async (transferSize: number): Promise<boolean> => {
            try {
                const authHeader = await getAuthHeader();
                // Backend expects size in MB; convert from bytes to MB
                const transferSizeMB = transferSize / (1024 * 1024);
                const response = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/usage/check-transfer`,
                    { transferSize: transferSizeMB },
                    { headers: authHeader }
                );
                return response.data === true;
            } catch (err: any) {
                console.error("Error checking transfer quota:", err);
                return false;
            }
        },
        []
    );

    const checkMultipleFilesQuota = useCallback(
        async (
            files: File[],
            uploadType?: string,
            postType?: string,
            originalVideoDuration?: number
        ): Promise<{
            canUpload: boolean;
            totalSize: number;
            storageOk: boolean;
            transferOk: boolean;
            videoLimitOk: boolean;
            reason?: string;
        }> => {
            // Calculate quota size considering video conversion
            const MAX_CONVERTED_VIDEO_SIZE = 200 * 1024 * 1024; // 200MB in bytes

            let totalQuotaSize = 0;
            let actualTotalSize = 0;
            let videoCount = 0;
            let imageCount = 0;

            files.forEach(file => {
                actualTotalSize += file.size;

                if (file.type.startsWith('video/')) {
                    // For videos, we only reserve up to 200MB regardless of original size
                    // because they get converted
                    const reservedSize = Math.min(file.size, MAX_CONVERTED_VIDEO_SIZE);
                    totalQuotaSize += reservedSize;
                    videoCount += 1;
                    console.log(`Video ${file.name}: Original ${file.size} bytes, Reserved ${reservedSize} bytes for quota check`);
                } else if (file.type.startsWith('image/')) {
                    // For images, use actual size and count them
                    totalQuotaSize += file.size;
                    imageCount += 1;
                } else {
                    // For other files, use actual size
                    totalQuotaSize += file.size;
                }
            });

            try {
                const [storageResult, transferOk] = await Promise.all([
                    checkStorageQuota(totalQuotaSize, videoCount, imageCount, uploadType, postType, originalVideoDuration),
                    checkTransferQuota(totalQuotaSize),
                ]);

                // storageOk already collapses non-captioned video-limit failures
                // (see checkStorageQuota), so existing callers keep their old
                // semantics. videoLimitOk is reserved for the captioned-video
                // flow to distinguish the ZapCap-seconds limit.
                const { storageOk, videoLimitOk } = storageResult;

                console.log("storageOk", storageOk, "videoLimitOk", videoLimitOk);
                console.log("transferOk", transferOk);

                let reason = "";
                if (!videoLimitOk) {
                    reason = "ZapCap captioning limit exceeded";
                } else if (!storageOk && !transferOk) {
                    reason = "Both storage and transfer quota exceeded";
                } else if (!storageOk) {
                    reason = "Storage quota exceeded";
                } else if (!transferOk) {
                    reason = "Transfer quota exceeded";
                }

                return {
                    canUpload: storageOk && videoLimitOk && transferOk,
                    totalSize: actualTotalSize, // Return actual total size for display
                    storageOk,
                    transferOk,
                    videoLimitOk,
                    reason: reason || undefined,
                };
            } catch (err: any) {
                console.error("Error checking multiple files quota:", err);
                return {
                    canUpload: false,
                    totalSize: actualTotalSize,
                    storageOk: false,
                    transferOk: false,
                    videoLimitOk: true,
                    reason: "Failed to check quota limits",
                };
            }
        },
        [checkStorageQuota, checkTransferQuota]
    );

    const formatBytes = useCallback(
        (valueInMB: number, decimals = 2): string => {
            // Interpret input as megabytes (MB) and return a formatted string in gigabytes (GB)
            if (valueInMB === 0) return "0 GB";

            const dm = decimals < 0 ? 0 : decimals;
            const valueInGB = valueInMB / 1024; // 1024 MB = 1 GB

            return `${parseFloat(valueInGB.toFixed(dm))} GB`;
        },
        []
    );

    const getQuotaPercentage = useCallback(
        (used: number, limit: number): number => {
            if (limit === 0) return 0;
            return Math.min(100, (used / limit) * 100);
        },
        []
    );

    return {
        quotaStatus,
        loading,
        error,
        fetchQuotaStatus,
        checkStorageQuota,
        checkTransferQuota,
        checkMultipleFilesQuota,
        formatBytes,
        getQuotaPercentage,
    };
};